import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ConfirmButton } from "@/components/common/ConfirmButton";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { ContextMenu, type ContextMenuPosition } from "@/components/common/ContextMenu";
import { IconButton } from "@/components/common/IconButton";
import { SectionHeader } from "@/components/common/SectionHeader";
import { Spinner } from "@/components/common/Spinner";
import { FileTreeNode } from "@/components/file-tree/FileTreeNode";
import {
	TREE_HOVER_GROUP_CLASS,
	TREE_INDENT_PX,
	TREE_ROOT_PADDING_PX,
	TreeIndentGuides,
} from "@/components/file-tree/TreeIndentGuides";
import { FileTreeSkeleton } from "@/components/file-tree/FileTreeSkeleton";
import { EnvironmentStoppedPlaceholder } from "@/components/file-tree/EnvironmentStoppedPlaceholder";
import { NewFileDialog } from "@/components/file-tree/NewFileDialog";
import { NewFolderDialog } from "@/components/file-tree/NewFolderDialog";
import { RenameDialog } from "@/components/file-tree/RenameDialog";
import { SharedVolumesTreeSection } from "@/components/file-tree/SharedVolumesTreeSection";
import { useEnvironmentUpload } from "@/components/file-tree/useEnvironmentUpload";
import { type TreeEntry, useEnvironmentTree } from "@/components/file-tree/useFileTree";
import { useCloudSharedVolumeDrop } from "@/components/volumes/useSharedVolumeDrop";
import {
	CREATE_TARGET_MISSING,
	createCloudSharedVolumeEntry,
} from "@/lib/cloud-shared-volume-entries";
import {
	isCloudSharedVolumeDirectoryLoaded,
	resolveFileTreeCreateTarget,
	type FileTreeCreateTarget,
} from "@/lib/cloud-shared-volumes";
import { canDropEnvironmentEntries } from "@/lib/dnd/environment-entry-drop";
import { useDropTarget } from "@/lib/dnd/useDropTarget";
import type { EnvironmentEntryDragItem, EnvironmentEntryPayload } from "@/lib/dnd/payload";
import {
	buildEnvironmentDeleteMessage,
	collectOpenEnvironmentFiles,
	countDirtyFilesUnder,
	createFileInEnvironment,
	createFolderInEnvironment,
	deleteEnvironmentEntries,
	downloadEnvironmentEntriesAsZip,
	environmentEntryExists,
	getEnvironmentEntryCreationParentPath,
	getTopLevelEnvironmentPaths,
	isSameOrDescendantPath,
	loadEnvironmentDirectory,
	moveEnvironmentEntries,
	refreshEnvironmentFiles,
	renameEnvironmentEntry,
	splitPath,
	uploadDroppedFilesToEnvironment,
	uploadFilesToActiveEnvironment,
} from "@/lib/environment-files";
import {
	flattenEntries,
	flattenVisibleRows,
	resolveCurrentRowPath,
	resolveExtendedSelection,
	resolveRowAfterRemoval,
	resolveTreeKeyAction,
	withExpandedAncestors,
} from "@/lib/file-tree-navigation";
import { STICKY_HEADER_STYLE } from "@/lib/pane-styles";
import { ROW_HOVER_CLASS } from "@/lib/row-styles";
import {
	selectEffectiveEnvironmentId,
	selectIsChatEnvironmentStopped,
	selectEnvironmentUploadProgress,
	selectIsWorkspaceUploadReady,
	useChatStore,
} from "@/stores/useChatStore";
import {
	refreshCloudSharedVolumes,
	useCloudSharedVolumeStore,
} from "@/stores/useCloudSharedVolumeStore";
import { relistDeviceSharedVolumes } from "@/stores/useDeviceSharedVolumeStore";
import { useToastStore } from "@/stores/useToastStore";

export interface EnvironmentFilesPaneProps {
	/** ドラフト環境の初期化または一覧取得に失敗しているか。 */
	preparationFailed: boolean;
	/** 失敗したドラフト環境の初期化を再試行するコールバック。 */
	retryPreparation: () => void;
	/** ワークスペース検索ペインを開くコールバック。 */
	onOpenSearch?: () => void;
	/** 見出しだけを残して折りたたんでいるか。 */
	collapsed: boolean;
	/** 見出しのトグルを押したときのコールバック。 */
	onToggleCollapsed: () => void;
}

function toDragItem(entry: TreeEntry): EnvironmentEntryDragItem {
	return { path: entry.path, entryType: entry.type, name: entry.name };
}

const selectHomeTree = () => useCloudSharedVolumeStore.getState().selectTree("home");

/**
 * ワークスペースのファイルツリーを描画するペイン。空白部分の右クリックで
 * コンテキストメニューを開き、ワークスペースルートへファイルをアップロードできる。
 */
export function EnvironmentFilesPane({
	preparationFailed,
	retryPreparation,
	onOpenSearch,
	collapsed,
	onToggleCollapsed,
}: EnvironmentFilesPaneProps) {
	const { tree: environmentFilesTree, rootHasMore } = useEnvironmentTree();
	// tree が null = 未読込 (スケルトン)、[] = 読込済みで空 (「ファイルがありません」)。
	// 並び順はサーバーが返した順 (ディレクトリ優先 + 名前順) をそのまま使う。
	const children = environmentFilesTree;
	const filesReady = environmentFilesTree !== null;
	const [menuPosition, setMenuPosition] = useState<ContextMenuPosition | null>(null);
	/** 開いている作成のダイアログと、開いたときに決めた作成先。 */
	const [creating, setCreating] = useState<{
		kind: "file" | "folder";
		target: FileTreeCreateTarget;
	} | null>(null);
	const [selectedPaths, setSelectedPaths] = useState<ReadonlySet<string>>(() => new Set());
	const [expansionOverrides, setExpansionOverrides] = useState<ReadonlyMap<string, boolean>>(
		() => new Map(),
	);
	const [renameTarget, setRenameTarget] = useState<TreeEntry | null>(null);
	const [deleteTarget, setDeleteTarget] = useState<{ paths: string[]; message: string } | null>(
		null,
	);
	const [revealSeq, setRevealSeq] = useState(0);
	const environmentId = useChatStore(selectEffectiveEnvironmentId);
	const environmentStatus = useChatStore((state) => state.environmentStatus);
	const environmentStopped = useChatStore(selectIsChatEnvironmentStopped);
	const showStoppedPlaceholder = environmentStopped && environmentStatus !== null;
	// 折りたたみ中は見出ししか出ておらず、環境停止中はツリーの代わりにプレースホルダーが出ている。
	// どちらも隠れているツリーへの操作 (ドロップ・右クリックメニュー・選択解除・検索) は受け付けない。
	const interactive = !collapsed && !environmentStopped;
	const uploadProgress = useChatStore((state) =>
		selectEnvironmentUploadProgress(state, environmentId),
	);
	const selectedEnvironmentFilesTreeNodePath = useChatStore(
		(state) => state.selectedEnvironmentFilesTreeNodePath,
	);
	const environmentFilesTreeNavigation = useChatStore(
		(state) => state.environmentFilesTreeNavigation,
	);
	const selectEnvironmentFilesTreeNode = useChatStore(
		(state) => state.selectEnvironmentFilesTreeNode,
	);
	const localSelectionTargetRef = useRef<string | null | undefined>(undefined);
	const selectionAnchorRef = useRef<string | null>(null);
	const revealTargetRef = useRef<{ path: string; focus: boolean } | null>(null);
	const treeRef = useRef<HTMLDivElement>(null);
	const visibleRows = useMemo(
		() => (children ? flattenVisibleRows(children, expansionOverrides, rootHasMore) : []),
		[children, expansionOverrides, rootHasMore],
	);
	const entriesByPath = useMemo(
		() => new Map((children ? flattenEntries(children) : []).map((entry) => [entry.path, entry])),
		[children],
	);
	const selectedEntries = useMemo(
		() =>
			[...selectedPaths]
				.map((path) => entriesByPath.get(path))
				.filter((entry): entry is TreeEntry => !!entry),
		[entriesByPath, selectedPaths],
	);
	const currentRowPath = resolveCurrentRowPath(visibleRows, selectedEnvironmentFilesTreeNodePath);
	// ドラフト環境の初期化中や環境未選択、作れない共有ボリュームの行を選んでいるときは、作成・アップロードの導線をまとめて塞ぐ。
	const cloudSelection = useCloudSharedVolumeStore((state) => state.selection);
	const workspaceReady = useChatStore(selectIsWorkspaceUploadReady);
	const canCreateEntry = workspaceReady && resolveFileTreeCreateTarget(cloudSelection, "") !== null;
	const { uploadPickedFilesTo, overwriteDialog: sharedVolumeOverwriteDialog } =
		useCloudSharedVolumeDrop();
	/** ファイル選択を開いたときに決めたアップロード先。 */
	const uploadTargetRef = useRef<FileTreeCreateTarget | null>(null);
	const uploadPickedFiles = useCallback(
		async (files: File[]) => {
			const target = uploadTargetRef.current;
			if (target?.kind === "sharedVolume") {
				const { volumes, directories } = useCloudSharedVolumeStore.getState();
				if (
					!isCloudSharedVolumeDirectoryLoaded(
						volumes,
						directories,
						target.volumeId,
						target.directory,
					)
				) {
					useToastStore
						.getState()
						.show({ type: "error", message: "アップロード先のフォルダが見つかりません。" });
					return;
				}
				// 失敗は知らせ済み。
				await uploadPickedFilesTo(target.volumeId, target.directory, files).catch(
					(err: unknown) => {
						console.error("共有ボリュームにアップロードできませんでした。", err);
					},
				);
			} else if (target !== null) {
				await uploadFilesToActiveEnvironment("", files);
			}
		},
		[uploadPickedFilesTo],
	);
	const { uploadInputs, uploadMenuItems: pickerMenuItems } = useEnvironmentUpload(
		"",
		"environment-upload",
		{ disabled: !canCreateEntry, upload: uploadPickedFiles },
	);
	const uploadMenuItems = pickerMenuItems.map((item) => ({
		...item,
		onSelect: () => {
			uploadTargetRef.current = resolveFileTreeCreateTarget(
				useCloudSharedVolumeStore.getState().selection,
				"",
			);
			item.onSelect?.();
		},
	}));

	const requestReveal = useCallback((path: string, focus: boolean) => {
		revealTargetRef.current = { path, focus };
		setRevealSeq((sequence) => sequence + 1);
	}, []);

	useEffect(() => {
		const target = revealTargetRef.current;
		if (!target) return;
		revealTargetRef.current = null;
		const row = [
			...(treeRef.current?.querySelectorAll<HTMLButtonElement>("[data-path]") ?? []),
		].find((element) => element.dataset.path === target.path);
		if (!row) return;
		if (target.focus) row.focus({ preventScroll: true });
		row.scrollIntoView({ block: "nearest" });
	}, [revealSeq]);

	useEffect(() => {
		setSelectedPaths(new Set());
		selectionAnchorRef.current = null;
		setExpansionOverrides(new Map());
		// 削除確認ダイアログを開いたまま環境が切り替わると、スナップショット済みの paths が
		// 別環境の environmentId に対して送られてしまうため、開いていれば閉じる。作成先も同じ。
		setDeleteTarget(null);
		setCreating(null);
	}, [environmentId]);

	useEffect(() => {
		const isSelfCaused = localSelectionTargetRef.current === selectedEnvironmentFilesTreeNodePath;
		localSelectionTargetRef.current = undefined;
		if (isSelfCaused) return;
		if (selectedEnvironmentFilesTreeNodePath !== null) selectHomeTree();
		selectionAnchorRef.current = selectedEnvironmentFilesTreeNodePath;
		setSelectedPaths(
			selectedEnvironmentFilesTreeNodePath === null
				? new Set()
				: new Set([selectedEnvironmentFilesTreeNodePath]),
		);
	}, [selectedEnvironmentFilesTreeNodePath]);

	useEffect(() => {
		const path = environmentFilesTreeNavigation.path;
		if (!path) return;
		setExpansionOverrides((current) => withExpandedAncestors(current, path));
		requestReveal(path, false);
	}, [environmentFilesTreeNavigation, requestReveal]);

	const clearSelection = useCallback(() => {
		setSelectedPaths(new Set());
		localSelectionTargetRef.current = null;
		selectionAnchorRef.current = null;
		selectEnvironmentFilesTreeNode(null);
	}, [selectEnvironmentFilesTreeNode]);

	// 共有ボリュームの行を選んだら (同じ行の押し直しでも)、~ の選択を外す。
	const selectedTree = useCloudSharedVolumeStore((state) => state.selectedTree);
	useEffect(() => {
		if (selectedTree.tree === "cloud") clearSelection();
	}, [clearSelection, selectedTree]);

	const selectSingleEntry = useCallback(
		(path: string) => {
			selectHomeTree();
			setSelectedPaths(new Set([path]));
			selectionAnchorRef.current = path;
			localSelectionTargetRef.current = path;
			selectEnvironmentFilesTreeNode(path);
		},
		[selectEnvironmentFilesTreeNode],
	);

	// ストア更新・ref 更新は setSelectedPaths の更新関数の外で行う。更新関数は React の
	// レンダー中に実行されるため、中でストアを更新すると他コンポーネント (FileUrlSync など) の
	// 更新をレンダー中に発火させてしまう。
	const selectEntry = useCallback(
		(path: string, event: React.MouseEvent) => {
			selectHomeTree();
			if (event.shiftKey) {
				const { paths, anchorPath } = resolveExtendedSelection(
					visibleRows,
					selectionAnchorRef.current,
					path,
					path,
				);
				if (paths.length > 0) {
					selectionAnchorRef.current = anchorPath;
					localSelectionTargetRef.current = path;
					selectEnvironmentFilesTreeNode(path);
					setSelectedPaths(new Set(paths));
					return;
				}
			}

			const isToggle = event.metaKey || event.ctrlKey;
			const next = new Set(isToggle ? selectedPaths : []);
			if (isToggle && next.has(path)) next.delete(path);
			else next.add(path);
			if (!isToggle) selectionAnchorRef.current = path;
			else if (selectionAnchorRef.current === null) selectionAnchorRef.current = path;
			localSelectionTargetRef.current = next.has(path) ? path : null;
			selectEnvironmentFilesTreeNode(next.has(path) ? path : null);
			setSelectedPaths(next);
		},
		[selectEnvironmentFilesTreeNode, visibleRows, selectedPaths],
	);

	const selectContextEntry = useCallback(
		(path: string) => {
			selectHomeTree();
			localSelectionTargetRef.current = path;
			selectionAnchorRef.current = path;
			selectEnvironmentFilesTreeNode(path);
			setSelectedPaths((current) => (current.has(path) ? current : new Set([path])));
		},
		[selectEnvironmentFilesTreeNode],
	);

	const toggleExpand = useCallback((path: string, expanded: boolean) => {
		// 折りたたみでアンカー行が隠れると範囲選択の基準を失うため、畳んだフォルダ自身へ寄せる。
		const anchor = selectionAnchorRef.current;
		if (!expanded && anchor !== null && isSameOrDescendantPath(anchor, path)) {
			selectionAnchorRef.current = path;
		}
		setExpansionOverrides((current) => {
			if (current.get(path) === expanded) return current;
			const next = new Map(current);
			next.set(path, expanded);
			return next;
		});
	}, []);

	/** 直下を取得中のディレクトリ (行にローディング表示を出すため)。 */
	const [loadingDirPaths, setLoadingDirPaths] = useState<ReadonlySet<string>>(() => new Set());
	const loadRequestsRef = useRef<Set<string>>(new Set());

	const loadDirectory = useCallback(
		(dirPath: string, offset: number) => {
			if (environmentId === null) return;
			const requestKey = `${dirPath}\n${offset}`;
			if (loadRequestsRef.current.has(requestKey)) return;
			loadRequestsRef.current.add(requestKey);
			setLoadingDirPaths((current) => new Set(current).add(dirPath));
			void (async () => {
				try {
					await loadEnvironmentDirectory(environmentId, dirPath, offset);
				} catch (err) {
					// 取得できなかったフォルダは開いたままにせず畳み、再試行できる状態へ戻す。
					toggleExpand(dirPath, false);
					useToastStore
						.getState()
						.show({ type: "error", message: "フォルダの中身を取得できませんでした。" });
					console.error("環境ファイルのフォルダ取得に失敗しました。", err);
					throw err;
				} finally {
					loadRequestsRef.current.delete(requestKey);
					setLoadingDirPaths((current) => {
						const next = new Set(current);
						next.delete(dirPath);
						return next;
					});
				}
			})();
		},
		[environmentId, toggleExpand],
	);

	// 展開されたのに直下が未取得のフォルダを取得する。ツリー操作だけでなく、検索・Quick Open・
	// エージェントの参照からの展開要求もここを通る。
	useEffect(() => {
		for (const row of visibleRows) {
			if (row.entry.type !== "directory" || row.entry.children !== null) continue;
			if (expansionOverrides.get(row.entry.path) !== true) continue;
			loadDirectory(row.entry.path, 0);
		}
	}, [expansionOverrides, loadDirectory, visibleRows]);

	const loadMoreEntries = useCallback(
		(dirPath: string) => {
			const loaded = useChatStore.getState().environmentFileChildren?.get(dirPath);
			loadDirectory(dirPath, loaded?.entries.length ?? 0);
		},
		[loadDirectory],
	);

	const getCreateTarget = useCallback(() => {
		const state = useChatStore.getState();
		return resolveFileTreeCreateTarget(
			useCloudSharedVolumeStore.getState().selection,
			getEnvironmentEntryCreationParentPath(
				state.selectedEnvironmentFilesTreeNodePath,
				state.environmentFileChildren,
			),
		);
	}, []);
	// 作成先が選択中のツリーノードと一致する場合に限らず常に展開する。選択は検索・Quick Open・
	// エージェントの参照など、ツリー上の展開操作を伴わずに設定されることもあるため。
	const expandCreatedFolder = useCallback((parentPath: string) => {
		if (parentPath) useChatStore.getState().navigateToEnvironmentFilesTreeNode(parentPath);
	}, []);
	const handleCreate = useCallback(
		async (name: string) => {
			if (environmentId === null || creating === null) throw new Error(CREATE_TARGET_MISSING);
			const { kind, target } = creating;
			if (target.kind === "sharedVolume") {
				await createCloudSharedVolumeEntry(
					environmentId,
					target.volumeId,
					target.directory,
					name,
					kind,
				);
			} else {
				await (kind === "folder" ? createFolderInEnvironment : createFileInEnvironment)(
					environmentId,
					target.parentPath,
					name,
				);
				expandCreatedFolder(target.parentPath);
			}
			setCreating(null);
		},
		[creating, environmentId, expandCreatedFolder],
	);
	const openCreateDialog = useCallback(
		(kind: "file" | "folder") => {
			const target = getCreateTarget();
			if (target !== null) setCreating({ kind, target });
		},
		[getCreateTarget],
	);
	const openNewFileDialog = useCallback(() => openCreateDialog("file"), [openCreateDialog]);
	const openNewFolderDialog = useCallback(() => openCreateDialog("folder"), [openCreateDialog]);
	const [refreshing, setRefreshing] = useState(false);
	const reloadTree = useCallback(async () => {
		if (environmentId === null) return;
		setRefreshing(true);
		relistDeviceSharedVolumes();
		refreshCloudSharedVolumes(environmentId);
		try {
			await refreshEnvironmentFiles(environmentId);
		} finally {
			setRefreshing(false);
		}
	}, [environmentId]);

	const createDragPayload = useCallback(
		(entry: TreeEntry): EnvironmentEntryPayload => ({
			kind: "environment-entry",
			entries: (selectedPaths.has(entry.path) ? selectedEntries : [entry]).map(toDragItem),
		}),
		[selectedEntries, selectedPaths],
	);

	const moveEntries = useCallback(
		(payload: EnvironmentEntryPayload, destinationPath: string) => {
			if (!environmentId) return;
			void (async () => {
				try {
					await moveEnvironmentEntries(
						environmentId,
						payload.entries.map((item) => item.path),
						destinationPath,
					);
					clearSelection();
				} catch (err) {
					// moveEnvironmentEntries 内で失敗トースト表示・再取得済み。再試行できるよう選択は保持しつつ、
					// D&D の onDrop から呼ばれる末端 (これより上位に await する呼び出し元がない) のため、
					// 上位で検知できるようログに残したうえで再送出する。
					console.error("環境ファイルの移動に失敗しました。", err);
					throw err;
				}
			})();
		},
		[clearSelection, environmentId],
	);

	/** OS からドロップされたファイル・フォルダを、指定ディレクトリへアップロードする。 */
	const uploadDroppedFiles = useCallback(
		(dataTransfer: DataTransfer, destinationDirPath: string) => {
			if (environmentId === null) return;
			uploadDroppedFilesToEnvironment(environmentId, dataTransfer, destinationDirPath, {
				isCurrent: () => selectEffectiveEnvironmentId(useChatStore.getState()) === environmentId,
			});
		},
		[environmentId],
	);

	const downloadSelectedAsZip = useCallback(() => {
		if (!environmentId || selectedEntries.length === 0) return;
		void (async () => {
			try {
				await downloadEnvironmentEntriesAsZip(
					environmentId,
					selectedEntries.map((entry) => entry.path),
				);
			} catch (err) {
				// downloadEnvironmentEntriesAsZip 内で失敗トースト表示済み。ログに残したうえで再送出する。
				console.error("環境ファイルのZIPダウンロードに失敗しました。", err);
				throw err;
			}
		})();
	}, [environmentId, selectedEntries]);

	/** 同じ環境のアップロード中は追加の OS ドロップを受け付けない。 */
	const osDropEnabled = workspaceReady && uploadProgress === null;
	const { isOver, dropProps } = useDropTarget({
		accept: "environment-entry",
		canDrop: (payload) =>
			filesReady && environmentId !== null && canDropEnvironmentEntries(payload, ""),
		onDrop: (payload) => moveEntries(payload, ""),
		acceptFiles: {
			canDrop: () => osDropEnabled,
			onDrop: (e) => uploadDroppedFiles(e.dataTransfer, ""),
		},
	});

	const requestDelete = useCallback((entries: readonly TreeEntry[]) => {
		if (entries.length === 0) return;

		const topLevelPaths = getTopLevelEnvironmentPaths(entries.map((entry) => entry.path));
		const topLevelPathSet = new Set(topLevelPaths);
		const topLevelEntries = entries.filter((entry) => topLevelPathSet.has(entry.path));
		const state = useChatStore.getState();
		const openFiles = collectOpenEnvironmentFiles(
			selectEffectiveEnvironmentId(state),
			state.environmentFileChildren,
			state.openFileEntries,
			state.openFileIds,
		);
		const dirtyFileCount = countDirtyFilesUnder(openFiles, state.dirtyFileIds, topLevelPaths);
		setDeleteTarget({
			// deleteEnvironmentEntries が自前で折りたたむため、ここでは生のパスを渡す
			// (downloadSelectedAsZip と同じ流儀)。
			paths: entries.map((entry) => entry.path),
			message: buildEnvironmentDeleteMessage(topLevelEntries, dirtyFileCount),
		});
	}, []);

	const requestDeleteSelected = useCallback(
		() => requestDelete(selectedEntries),
		[requestDelete, selectedEntries],
	);

	const handleRowKeyDown = useCallback(
		(event: React.KeyboardEvent<HTMLButtonElement>, path: string) => {
			if (event.nativeEvent.isComposing) return;
			if (
				document.querySelector(
					'[data-testid="file-node-menu"], [data-testid="environment-pane-menu"]',
				)
			) {
				return;
			}
			const action = resolveTreeKeyAction({
				key: event.key,
				shiftKey: event.shiftKey,
				metaKey: event.metaKey,
				ctrlKey: event.ctrlKey,
				altKey: event.altKey,
				rows: visibleRows,
				currentPath: path,
				// ツリーから消えたパスが selectedPaths に残ることがあるため、実在する件数で判定する。
				selectedCount: selectedEntries.length,
			});
			if (!action) return;
			event.preventDefault();
			switch (action.type) {
				case "selectAll": {
					if (entriesByPath.size === 0) break;
					selectHomeTree();
					selectionAnchorRef.current = path;
					setSelectedPaths(new Set(entriesByPath.keys()));
					break;
				}
				case "move": {
					// 範囲が解決できないとき (アンカーが可視行にない等) は単一選択へ退避し、
					// 選択が空のままフォーカスだけ進む状態を避ける。
					const { paths, anchorPath } = action.extend
						? resolveExtendedSelection(visibleRows, selectionAnchorRef.current, path, action.path)
						: { paths: [], anchorPath: path };
					if (paths.length > 0) {
						selectHomeTree();
						selectionAnchorRef.current = anchorPath;
						setSelectedPaths(new Set(paths));
						localSelectionTargetRef.current = action.path;
						selectEnvironmentFilesTreeNode(action.path);
					} else {
						selectSingleEntry(action.path);
					}
					requestReveal(action.path, true);
					break;
				}
				case "setExpanded":
					toggleExpand(action.path, action.expanded);
					break;
				case "rename":
					setRenameTarget(entriesByPath.get(action.path) ?? null);
					break;
				case "delete": {
					// 削除は選択中の全件が対象。選択が空のときだけフォーカス行へ退避する。
					const entry = entriesByPath.get(action.path);
					requestDelete(selectedEntries.length > 0 ? selectedEntries : entry ? [entry] : []);
					break;
				}
				case "clearSelection":
					clearSelection();
					break;
				case "suppress":
					break;
			}
		},
		[
			clearSelection,
			entriesByPath,
			requestDelete,
			requestReveal,
			selectEnvironmentFilesTreeNode,
			selectSingleEntry,
			selectedEntries,
			toggleExpand,
			visibleRows,
		],
	);

	const handleContextMenu = useCallback((event: React.MouseEvent) => {
		event.preventDefault();
		setMenuPosition({ x: event.clientX, y: event.clientY });
	}, []);
	// ツリー項目のない空白部分のクリックで選択を解除する。子要素上のクリックは
	// event.target !== event.currentTarget になるためここでは無視される。
	const handlePaneClick = useCallback(
		(event: React.MouseEvent) => {
			if (event.target !== event.currentTarget) return;
			clearSelection();
			selectHomeTree();
		},
		[clearSelection],
	);

	return (
		<div
			style={{ minHeight: "100%" }}
			onClick={interactive ? handlePaneClick : undefined}
			onContextMenu={interactive ? handleContextMenu : undefined}
			{...(interactive ? dropProps : {})}
			/* 枠をここに描くと sticky 見出しの不透明背景に上端が覆われ、スクロールで枠ごと
			   流れる。そのため属性だけ立て、描画はスクロール枠側 (globals.css の
			   izanagi-environment-drop-frame) に任せる。折りたたみ中はツリーが隠れているので出さない。 */
			data-dragover={isOver && interactive ? "" : undefined}
		>
			{uploadInputs}
			<div style={STICKY_HEADER_STYLE}>
				<SectionHeader
					label="ファイル"
					collapsed={collapsed}
					onToggleCollapsed={onToggleCollapsed}
					toggleTestId="environment-files-toggle"
				>
					{interactive && (
						<div className="flex" data-testid="environment-file-create-actions">
							<IconButton
								icon="filePlus"
								color="var(--fg-3)"
								disabled={!canCreateEntry}
								onClick={openNewFileDialog}
								title="新規ファイル"
								data-testid="environment-new-file-button"
							/>
							<IconButton
								icon="folderPlus"
								color="var(--fg-3)"
								disabled={!canCreateEntry}
								onClick={openNewFolderDialog}
								title="新規フォルダ"
								data-testid="environment-new-folder-button"
							/>
						</div>
					)}
					{interactive && (
						<IconButton
							icon="rotate"
							color="var(--fg-3)"
							disabled={!filesReady || refreshing}
							onClick={() => void reloadTree()}
							title="再読み込み"
							data-testid="environment-files-reload-button"
						/>
					)}
					{interactive && onOpenSearch && (
						<IconButton
							icon="search"
							color="var(--fg-3)"
							onClick={onOpenSearch}
							title="ワークスペースを検索"
							data-testid="open-workspace-search"
						/>
					)}
				</SectionHeader>
			</div>
			{!collapsed && !showStoppedPlaceholder && <SharedVolumesTreeSection />}
			{!collapsed &&
				(showStoppedPlaceholder ? (
					<EnvironmentStoppedPlaceholder status={environmentStatus} />
				) : preparationFailed ? (
					<div
						data-testid="environment-files-preparation-error"
						style={{ padding: "8px 12px 12px", color: "var(--danger)" }}
					>
						<p style={{ margin: "0 0 8px", fontSize: "var(--text-caption)" }}>
							エージェントのファイルの読み込みに失敗しました。
						</p>
						<ConfirmButton
							label="再試行"
							loading={false}
							variant="secondary"
							size="sm"
							onClick={retryPreparation}
							data-testid="environment-files-retry"
						/>
					</div>
				) : children === null ? (
					<FileTreeSkeleton />
				) : children.length > 0 ? (
					<div
						ref={treeRef}
						className={TREE_HOVER_GROUP_CLASS}
						data-testid="file-tree-environment"
						style={{ paddingBottom: 4 }}
					>
						{visibleRows.map((row) => (
							<Fragment key={row.entry.path}>
								<FileTreeNode
									entry={row.entry}
									depth={row.depth}
									expanded={row.expanded}
									loading={loadingDirPaths.has(row.entry.path)}
									isCurrent={row.entry.path === currentRowPath}
									selectedPaths={selectedPaths}
									onSelect={selectEntry}
									onContextSelect={selectContextEntry}
									onToggleExpand={toggleExpand}
									onRowKeyDown={handleRowKeyDown}
									onRequestRename={setRenameTarget}
									createDragPayload={createDragPayload}
									onMoveEntries={moveEntries}
									onDropOsFiles={uploadDroppedFiles}
									osDropEnabled={osDropEnabled}
									onDownloadSelected={downloadSelectedAsZip}
									onDeleteSelected={requestDeleteSelected}
								/>
								{row.loadMore?.map((more) => (
									<button
										key={more.dirPath}
										type="button"
										onClick={() => loadMoreEntries(more.dirPath)}
										disabled={loadingDirPaths.has(more.dirPath)}
										className={`relative flex min-h-[22px] w-full min-w-0 cursor-pointer items-center py-0.5 text-left text-list text-fg-2 ${ROW_HOVER_CLASS}`}
										style={{
											paddingLeft:
												more.depth * TREE_INDENT_PX + TREE_ROOT_PADDING_PX + TREE_INDENT_PX,
										}}
										data-testid={`file-tree-load-more-${more.dirPath || "root"}`}
									>
										<TreeIndentGuides depth={more.depth} />
										{loadingDirPaths.has(more.dirPath) ? <Spinner /> : "さらに表示"}
									</button>
								))}
							</Fragment>
						))}
					</div>
				) : (
					<p
						style={{
							padding: "4px 12px 8px",
							fontSize: "var(--text-caption)",
							color: "var(--fg-3)",
						}}
					>
						ファイルがありません
					</p>
				))}
			{menuPosition && (
				<ContextMenu
					position={menuPosition}
					onClose={() => setMenuPosition(null)}
					testId="environment-pane-menu"
					items={[
						{
							label: "新規ファイル",
							testId: "environment-pane-menu-new-file",
							disabled: !canCreateEntry,
							onSelect: openNewFileDialog,
						},
						{
							label: "新規フォルダ",
							testId: "environment-pane-menu-new-folder",
							disabled: !canCreateEntry,
							onSelect: openNewFolderDialog,
						},
						{ separator: true as const },
						...uploadMenuItems,
					]}
				/>
			)}
			{sharedVolumeOverwriteDialog}
			<NewFolderDialog
				open={creating?.kind === "folder"}
				onConfirm={handleCreate}
				onCancel={() => setCreating(null)}
			/>
			<NewFileDialog
				open={creating?.kind === "file"}
				onConfirm={handleCreate}
				onCancel={() => setCreating(null)}
			/>
			<RenameDialog
				open={renameTarget !== null}
				initialName={renameTarget?.name ?? ""}
				onConfirm={async (newName) => {
					if (!renameTarget || !environmentId) return;
					const { dir } = splitPath(renameTarget.path);
					const nextPath = dir ? `${dir}/${newName}` : newName;
					await renameEnvironmentEntry(environmentId, renameTarget.path, newName);
					const loaded = useChatStore.getState().environmentFileChildren;
					const path = environmentEntryExists(loaded, renameTarget.path)
						? renameTarget.path
						: environmentEntryExists(loaded, nextPath)
							? nextPath
							: renameTarget.path;
					setRenameTarget(null);
					selectSingleEntry(path);
					requestReveal(path, true);
				}}
				onCancel={() => {
					if (renameTarget) requestReveal(renameTarget.path, true);
					setRenameTarget(null);
				}}
			/>
			<ConfirmDialog
				open={deleteTarget !== null}
				title="削除の確認"
				message={deleteTarget?.message ?? ""}
				confirmLabel="削除"
				onConfirm={async () => {
					if (!deleteTarget || !environmentId) return;
					const nextPath = resolveRowAfterRemoval(visibleRows, deleteTarget.paths);
					await deleteEnvironmentEntries(environmentId, deleteTarget.paths);
					// 削除中に別環境へ切り替わっていた場合、切替時の useEffect が選択とダイアログを
					// 解除済みのため、ここでの解除は切替後の環境への誤操作になる。
					if (selectEffectiveEnvironmentId(useChatStore.getState()) !== environmentId) return;
					setDeleteTarget(null);
					if (nextPath) {
						selectSingleEntry(nextPath);
						requestReveal(nextPath, true);
					} else {
						clearSelection();
					}
				}}
				onCancel={() => {
					// ダイアログにフォーカスを奪われているため、操作の起点だった現在行へ戻す。
					if (currentRowPath) requestReveal(currentRowPath, true);
					setDeleteTarget(null);
				}}
			/>
		</div>
	);
}
